import React from 'react';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Checkbox from '@mui/material/Checkbox';
import Box from '@mui/material/Box';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useTheme } from '@mui/material/styles';

import { ColumnDef, SortModel } from './ServiceTable.types';

export interface ServiceTableHeadProps {
  columns: ColumnDef[];
  sortModel: SortModel;
  onSort: (field: string) => void;
  allSelected: boolean;
  someSelected: boolean;
  onSelectAll: (checked: boolean) => void;
}

const HEADER_SX = {
  fontSize: '10px',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.5px',
  fontWeight: 600,
  color: 'text.secondary',
  cursor: 'pointer',
  userSelect: 'none' as const,
  whiteSpace: 'nowrap' as const,
  py: 0.75,
  px: 1,
  borderBottom: '2px solid',
  borderColor: 'v4.border',
} as const;

export const ServiceTableHead: React.FC<ServiceTableHeadProps> = ({
  columns,
  sortModel,
  onSort,
  allSelected,
  someSelected,
  onSelectAll,
}) => {
  const theme = useTheme();
  const visibleColumns = columns.filter((c) => c.visible);

  return (
    <TableHead>
      <TableRow sx={{ bgcolor: 'v4.tableEven' }}>
        <TableCell padding="checkbox" sx={{ ...HEADER_SX, cursor: 'default' }}>
          <Checkbox
            size="small"
            checked={allSelected}
            indeterminate={someSelected && !allSelected}
            onChange={(e) => onSelectAll(e.target.checked)}
            slotProps={{ input: { 'aria-label': 'Select all rows' } }}
          />
        </TableCell>
        {visibleColumns.map((col) => {
          const isSorted = sortModel.field === col.key;
          return (
            <TableCell
              key={col.key}
              onClick={() => onSort(col.key)}
              sx={{
                ...HEADER_SX,
                color: isSorted ? theme.palette.v4.accent : 'text.secondary',
                ...(col.width ? { minWidth: col.width, maxWidth: col.width } : {}),
              }}
            >
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                {col.label}
                {isSorted && (
                  sortModel.direction === 'asc'
                    ? <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                    : <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                )}
              </Box>
            </TableCell>
          );
        })}
        <TableCell
          sx={{
            ...HEADER_SX,
            cursor: 'default',
            width: 70,
            position: 'sticky',
            right: 0,
            bgcolor: 'v4.tableEven',
            zIndex: 2,
            boxShadow: '-4px 0 8px rgba(0,0,0,0.04)',
          }}
          aria-label="Actions"
        />
      </TableRow>
    </TableHead>
  );
};
